import React, { useState } from "react";
import PropTypes from "prop-types";
import { NeoSideMenu, NeoSearch, NeoButton, NeoTabs } from "../../../../../components/Common";
import { getWorkoutTypeConfig } from "../../constants";
import MemberSelector from "../editor/MemberSelector";

const TABS = [
    { id: "templates", label: "Modelos", icon: "mdi-clipboard-text-outline" },
    { id: "prescriptions", label: "Prescrições", icon: "mdi-account-clock-outline" },
];

/**
 * Lista de prescrições — usa NeoSideMenu com NeoTabs no header e avatares de alunos.
 */
const PrescriptionList = ({
    prescriptions = [],
    activeId,
    onSelect,
    loading = false,
    filterMember = null,
    onFilterMember,
    onDirectPrescription,
    activeTab,
    onChangeTab,
    templatesBadge = 0,
    prescriptionsBadge = 0,
}) => {
    const [searchQuery, setSearchQuery] = useState("");

    const statusConfig = {
        active: { label: "Ativo", color: "success" },
        completed: { label: "Concluído", color: "info" },
        expired: { label: "Vencido", color: "warning" },
    };

    const formatDateBR = (dateString) => {
        if (!dateString) return "";
        const parts = dateString.split("-");
        if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
        return dateString;
    };

    const tabs = TABS.map(t => ({
        ...t,
        badge: t.id === "templates" ? (templatesBadge > 0 ? templatesBadge : null)
            : (prescriptionsBadge > 0 ? prescriptionsBadge : null),
    }));

    const searchComponent = (
        <div className="d-flex flex-column gap-2">
            {/* Tabs — navegação Modelos | Prescrições */}
            <NeoTabs
                activeTab={activeTab}
                onChangeTab={onChangeTab}
                tabs={tabs}
                style={{ marginBottom: "0px" }}
            />

            {/* Seletor de aluno */}
            <MemberSelector
                selectedMember={filterMember}
                onSelect={onFilterMember}
                onClear={() => onFilterMember(null)}
                label="Filtrar por Aluno"
            />

            {filterMember && (
                <NeoButton
                    variant="outline-chocolate"
                    className="w-100 mt-1"
                    icon="mdi-plus-circle-outline"
                    onClick={onDirectPrescription}
                    size="sm"
                >
                    Novo Treino
                </NeoButton>
            )}

            {filterMember && (
                <div className="mt-1">
                    <NeoSearch
                        size="sm"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Buscar prescrições..."
                    />
                </div>
            )}
        </div>
    );

    // Filtrar localmente por busca textual se houver termo
    const visiblePrescriptions = prescriptions.filter(presc => 
        !searchQuery || presc.templateTitle?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Mapear prescrições para o formato padrão do NeoSideMenu com foto/avatar do aluno
    const sideMenuItems = visiblePrescriptions.map((presc) => {
        const typeConfig = getWorkoutTypeConfig(presc.workoutType);
        const status = statusConfig[presc.status] || statusConfig.active;
        const dateRange = presc.startDate && presc.endDate 
            ? ` (${formatDateBR(presc.startDate)} → ${formatDateBR(presc.endDate)})`
            : "";

        return {
            id: presc.id,
            label: presc.memberName || "Aluno",
            description: `${presc.templateTitle || "Treino"}${dateRange}`,
            photoUrl: presc.memberPhoto || null, // Renderiza o avatar real do aluno!
            icon: typeConfig.icon,
            badge: status.label,
            badgeVariant: status.color
        };
    });

    return (
        <NeoSideMenu
            title="Prescrições"
            subtitle={filterMember ? `${prescriptions.length} prescrição${prescriptions.length !== 1 ? "ões" : ""}` : "Selecione um aluno"}
            icon="mdi-account-clock-outline"
            loading={loading}
            items={filterMember ? sideMenuItems : []}
            activeId={activeId}
            onSelect={(id) => {
                const presc = prescriptions.find(p => p.id === id);
                if (presc) onSelect(presc);
            }}
            searchComponent={searchComponent}
        />
    );
};

PrescriptionList.propTypes = {
    prescriptions: PropTypes.array,
    activeId: PropTypes.string,
    onSelect: PropTypes.func,
    onDelete: PropTypes.func,
    loading: PropTypes.bool,
    filterMember: PropTypes.object,
    onFilterMember: PropTypes.func,
    onDirectPrescription: PropTypes.func,
    activeTab: PropTypes.string,
    onChangeTab: PropTypes.func,
    templatesBadge: PropTypes.number,
    prescriptionsBadge: PropTypes.number,
};

export default PrescriptionList;
